import { apiFetch } from "../../lib/apiBase";
import React, { useState, useEffect } from 'react';
import { Bell, Search, Menu, LogIn, LogOut, Settings, FlaskConical, X, Loader2, Sun, Moon, Monitor, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import BrandWordmark from '../brand/BrandWordmark';
import { getMemberSiteUrl } from '../../lib/memberSiteUrl';

const Header = ({ onMenuClick = () => {}, isLoggedIn = false, user = null, onSearch, forcePublic = false, onOpenSettings = null }) => {
  const { logout, isAdmin, user: authUser, session, isAuthenticated } = useAuth();
  const { theme, setTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const sessionUser = session?.user;

  // Source of truth: Supabase session user, then AuthContext/backend user, then prop fallback.
  const effectiveUser = {
    email: sessionUser?.email || authUser?.email || user?.email || "",
    name:
      sessionUser?.user_metadata?.full_name ||
      sessionUser?.user_metadata?.name ||
      authUser?.name ||
      authUser?.email ||
      user?.name ||
      user?.email ||
      sessionUser?.email ||
      "",
    picture:
      sessionUser?.user_metadata?.avatar_url ||
      sessionUser?.user_metadata?.picture ||
      authUser?.picture ||
      authUser?.avatar_url ||
      user?.picture ||
      user?.avatar_url ||
      "",
    tier: authUser?.tier || user?.tier || "member",
  };
  const effectiveIsLoggedIn = forcePublic ? false : (!!isAuthenticated || !!isLoggedIn);
  const fallbackLetter =
    (effectiveUser?.name || effectiveUser?.email || "").trim().charAt(0).toUpperCase() || "?";

  const isBetaTester = effectiveUser?.tier === "beta_tester";
  const isPublic = !effectiveIsLoggedIn;

  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [markingRead, setMarkingRead] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);

  // Top nav items (PUBLIC preview only). Prospects can see + hover; clicks are blocked.
  const topNav = [
  {
    id: "start-here",
    label: "Start Here",
    tooltip:
      "A quick look at what Dad Partners is, who it’s for, and how the weekly rhythm works—so you can decide if this brotherhood fits you. Get clarity fast.",
  },
  {
    id: "community",
    label: "Community",
    tooltip:
      "Real conversations with Christian dads about parenting, marriage, and work pressure—honest, practical, and encouraging. You’ll leave lighter and clearer.",
  },
  {
    id: "wins",
    label: "Wins",
    tooltip:
      "A place to celebrate progress—small victories, answered prayers, tough weeks survived—and get the boost you need to keep going. Momentum beats motivation.",
  },
  {
    id: "calendar",
    label: "Calendar",
    tooltip:
      "Your weekly rhythm at a glance—live calls, guide partners, prompts, and upcoming meetups so you always know what’s next. Show up ready.",
  },
];

const handleTopNavClick = (item) => {
    toast("Members-only", { description: `Log in (or join) to access ${item.label}.` });
  };

  const handleOpenSettings = () => {
    if (typeof onOpenSettings === 'function') {
      onOpenSettings();
      return;
    }
    navigate('/settings');
  };

  useEffect(() => {
    if (
      process.env.NODE_ENV === "development" &&
      isAuthenticated &&
      !effectiveUser.email &&
      !effectiveUser.name
    ) {
      console.warn("[Header] Authenticated but no effectiveUser identity", { user: authUser, sessionUser });
    }
  }, [authUser, effectiveUser.email, effectiveUser.name, isAuthenticated, sessionUser]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (!showNotifications || !effectiveIsLoggedIn) return;

    let cancelled = false;

    const fetchNotifications = async () => {
      setLoadingNotifications(true);
      try {
        // IMPORTANT: use apiFetch so Authorization: Bearer ... is included
        const response = await apiFetch('/notifications');

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          console.error('Failed to fetch notifications:', err);
          return;
        }

        const data = await response.json();
        if (cancelled) return;

        setNotifications(
          (Array.isArray(data) ? data : []).map((n) => ({
            id: n.notification_id,
            title: n.title,
            message: n.message,
            time: formatTimeAgo(new Date(n.created_at)),
            read: n.read,
          }))
        );
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      } finally {
        if (!cancelled) {
          setLoadingNotifications(false);
        }
      }
    };

    fetchNotifications();

    return () => {
      cancelled = true;
    };
  }, [showNotifications, effectiveIsLoggedIn]);

  const formatTimeAgo = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = async (notifId) => {
    try {
      const res = await apiFetch(`/notifications/${notifId}/read`, {
        method: "POST",
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error("Failed to mark notification as read:", err);
        return;
      }

      setNotifications((prev) =>
        prev.map((n) => (n.id === notifId ? { ...n, read: true } : n))
      );
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingRead(true);
    try {
      const response = await apiFetch("/notifications/read-all", {
        method: "POST",
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        console.error("Failed to mark all as read:", err);
        toast.error(err?.detail || "Failed to mark notifications as read");
        return;
      }

      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      toast.success("All notifications marked as read");
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      toast.error("Failed to mark notifications as read");
    } finally {
      setMarkingRead(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-charcoal border-b border-charcoal-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left section */}
          <div className="flex items-center gap-4">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            <a href="/start-here" className="flex items-center gap-2">
              <BrandWordmark textClassName="text-lg" />
            </a>
          </div>

          {/* Center section - top nav (always visible on desktop) */}
          {!effectiveIsLoggedIn && (
          <nav className="hidden md:flex items-center gap-1">
            {topNav.map((item) => (
              <div key={item.id} className="relative group">
                <button
                  type="button"
                  onClick={() => handleTopNavClick(item)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isPublic
                      ? 'text-muted-foreground hover:text-foreground'
                      : 'text-foreground/80 hover:text-foreground hover:bg-charcoal-light'
                  }`}
                >
                  {item.label}
                </button>

                {/* Hover rollover */}
                <div className="pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 top-full mt-2 w-72 rounded-xl border border-charcoal-lighter bg-white p-3 text-xs text-slate-700 shadow-lg dark:bg-charcoal-light dark:text-muted-foreground">
                  <div className="font-semibold mb-1 text-slate-900 dark:text-foreground">{item.label}</div>
                  <div className="text-slate-700 dark:text-muted-foreground">{item.tooltip}</div>
                  <div className="mt-2 text-slate-600 dark:text-muted-foreground">Log in to access this section.</div>
                </div>
              </div>
            ))}
          </nav>
          )}
          {/* Spacer to push right section */}
          <div className="flex-1" />

          {/* Right section */}
          <div className="flex items-center gap-3">
            {effectiveIsLoggedIn ? (
              <a
                href={getMemberSiteUrl()}
                aria-label="Member site"
                title="Member site"
                className="inline-flex items-center gap-1.5 rounded-lg border border-charcoal-lighter p-2 text-sm font-medium text-foreground/80 transition-colors hover:border-aqua/40 hover:text-foreground sm:px-3 sm:py-1.5"
              >
                <ExternalLink className="h-4 w-4" />
                <span className="hidden sm:inline">Member site</span>
              </a>
            ) : null}
            {/* Search */}
            <div className="hidden md:flex w-64">
              <form
                className="relative w-full"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (searchQuery.trim() && onSearch) {
                    onSearch(searchQuery.trim());
                  }
                }}
              >
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  id="search-discussions"
                  name="search-discussions"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search discussions..."
                  className="w-full bg-charcoal-light border border-charcoal-lighter rounded-lg pl-10 pr-4 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-aqua transition-colors"
                />
              </form>
            </div>

            {/* Theme Toggle */}
            {/* ... keep the rest of your existing file exactly as it was ... */}

            {/* Theme Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 text-muted-foreground hover:text-foreground transition-colors">
                  {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-charcoal-light border-charcoal-lighter">
                <DropdownMenuItem
                  onClick={() => setTheme('light')}
                  className={`cursor-pointer ${theme === 'light' ? 'text-aqua' : 'text-muted-foreground'} hover:text-foreground hover:bg-charcoal-lighter`}
                >
                  <Sun className="w-4 h-4 mr-2" />
                  Light
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTheme('dark')}
                  className={`cursor-pointer ${theme === 'dark' ? 'text-aqua' : 'text-muted-foreground'} hover:text-foreground hover:bg-charcoal-lighter`}
                >
                  <Moon className="w-4 h-4 mr-2" />
                  Dark
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTheme('system')}
                  className={`cursor-pointer ${theme === 'system' ? 'text-aqua' : 'text-muted-foreground'} hover:text-foreground hover:bg-charcoal-lighter`}
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  System
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {effectiveIsLoggedIn ? (
              <>
                {/* Notifications Bell */}
                <div className="relative">
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative p-2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-aqua rounded-full"></span>
                    )}
                  </button>

                  {/* Notifications Dropdown */}
                  {showNotifications && (
                    <div className="absolute right-0 mt-2 w-80 bg-charcoal border border-charcoal-lighter rounded-xl shadow-xl z-50 overflow-hidden">
                      <div className="p-4 border-b border-charcoal-lighter flex items-center justify-between">
                        <h3 className="font-semibold text-foreground">Notifications</h3>
                        <button
                          onClick={() => setShowNotifications(false)}
                          className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {loadingNotifications ? (
                          <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 text-aqua animate-spin" />
                          </div>
                        ) : notifications.length === 0 ? (
                          <div className="p-4 text-center text-muted-foreground">
                            No notifications yet
                          </div>
                        ) : (
                          notifications.map((notif) => (
                            <div
                              key={notif.id}
                              onClick={() => handleNotificationClick(notif.id)}
                              className={`p-4 border-b border-charcoal-lighter hover:bg-charcoal-light cursor-pointer transition-colors ${!notif.read ? 'bg-aqua/5' : ''}`}
                            >
                              <div className="flex items-start gap-3">
                                {!notif.read && (
                                  <span className="w-2 h-2 rounded-full bg-aqua mt-2 flex-shrink-0"></span>
                                )}
                                <div className={!notif.read ? '' : 'ml-5'}>
                                  <p className="text-foreground text-sm font-medium">{notif.title}</p>
                                  <p className="text-muted-foreground text-xs mt-1">{notif.message}</p>
                                  <p className="text-muted-foreground text-xs mt-2">{notif.time}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="p-3 border-t border-charcoal-lighter">
                        <button
                          onClick={handleMarkAllRead}
                          disabled={markingRead || unreadCount === 0}
                          className="w-full text-center text-aqua text-sm hover:text-aqua-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {markingRead ? 'Marking...' : 'Mark all as read'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2">
                      <Avatar className="w-9 h-9 border-2 border-aqua cursor-pointer">
                        <AvatarImage src={effectiveUser?.picture} alt={effectiveUser?.name} />
                        <AvatarFallback className="bg-aqua text-charcoal">
                          {fallbackLetter}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="w-56 bg-charcoal border-charcoal-lighter">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-foreground">{effectiveUser?.name}</p>
                      <p className="text-xs text-muted-foreground">{effectiveUser?.email}</p>
                    </div>

                    <DropdownMenuSeparator className="bg-charcoal-lighter" />

                    <DropdownMenuItem
                      className="text-muted-foreground hover:text-foreground hover:bg-charcoal-lighter cursor-pointer"
                      onClick={handleOpenSettings}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Site Settings
                    </DropdownMenuItem>

                    {isBetaTester && (
                      <DropdownMenuItem
                        className="text-pink-500 hover:text-pink-400 hover:bg-charcoal-lighter cursor-pointer"
                        onClick={() => navigate("/beta")}
                      >
                        <FlaskConical className="w-4 h-4 mr-2" />
                        Beta Testing
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator className="bg-charcoal-lighter" />

                    <DropdownMenuItem
                      className="text-red-400 hover:text-red-300 hover:bg-charcoal-lighter cursor-pointer"
                      onSelect={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("[UI] Sign Out onSelect fired");
                        logout();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log("[UI] Sign Out onClick fired");
                        logout();
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => navigate('/login?mode=login')}
                  variant="outline"
                  className="border border-aqua/50 text-aqua hover:bg-aqua/15 hover:text-white dark:hover:text-white font-medium gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Login</span>
                </Button>
                <Button
                  onClick={() => navigate('/login?mode=register')}
                  className="bg-aqua hover:bg-aqua-dark text-charcoal font-medium gap-2"
                >
                  <span>Join</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
